/**
 * AdvertiserCapsPage — /advertisers/caps (feature #147).
 *
 * Per-advertiser cap configuration UI. Lists the advertiser's campaigns
 * grouped by `campaign_id`, with editable cap rows and consumed/limit
 * progress bars. Matches the brand tokens from the advertiser-portal and
 * other staging-main pages (zinc borders, emerald/sky gradient headline,
 * 6-8px rounded cards).
 *
 * Keeping the page self-contained:
 *   - no @tanstack/react-query; a single `Promise.allSettled`-style
 *     `useEffect` hydrates the caps list.
 *   - `useState`-driven edit form per row; submissions route through the
 *     shared `api.ts` wrappers.
 *   - the advertiser id is read from the TanStack Router search params
 *     (`?advertiser=<id>`). When missing, the page shows a prompt instead
 *     of triggering an empty fetch.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
	type AdvertiserCapRow,
	type CampaignGroup,
	type CapFormValues,
	type CapStatus,
	type CapType,
	type WindowKey,
	CAP_TYPES,
	CAP_TYPE_LABEL,
	WINDOW_KEYS,
	WINDOW_LABEL,
	advertiserCapsPaths,
	capFormSchema,
	fetchAdvertiserCaps,
	formatConsumedOverLimit,
	groupByCampaign,
	progressColor,
	progressPercent,
	upsertAdvertiserCap,
} from "./api";

// ---------------------------------------------------------------------------
// Brand tokens — mirrors campaigns.os-targeting + advertiser-portal.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-green-600 bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-green-700 hover:border-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Types / props
// ---------------------------------------------------------------------------

export interface AdvertiserCapsPageProps {
	/** Injected for tests + server-render smoke. Routes call without props. */
	initialAdvertiserId?: string;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AdvertiserCapsPage(props: AdvertiserCapsPageProps = {}) {
	const [advertiserId, setAdvertiserId] = useState(
		props.initialAdvertiserId ?? "",
	);
	const [draftAdvertiserId, setDraftAdvertiserId] = useState(
		props.initialAdvertiserId ?? "",
	);
	const [rows, setRows] = useState<AdvertiserCapRow[]>([]);
	const [loading, setLoading] = useState<boolean>(Boolean(advertiserId));
	const [error, setError] = useState<string | null>(null);
	const [flash, setFlash] = useState<string | null>(null);

	const reload = useCallback(async (id: string) => {
		if (!id) {
			setRows([]);
			setLoading(false);
			setError(null);
			return;
		}
		setLoading(true);
		setError(null);
		try {
			const next = await fetchAdvertiserCaps(id);
			setRows(next);
			setLoading(false);
		} catch (err) {
			setLoading(false);
			setError(err instanceof Error ? err.message : "Unable to load caps.");
		}
	}, []);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			if (cancelled) return;
			await reload(advertiserId);
		})();
		return () => {
			cancelled = true;
		};
	}, [advertiserId, reload]);

	const groups: CampaignGroup[] = useMemo(() => groupByCampaign(rows), [rows]);

	const onLoadAdvertiser = useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			setAdvertiserId(draftAdvertiserId.trim());
			setFlash(null);
		},
		[draftAdvertiserId],
	);

	const onCapSaved = useCallback(
		async (message: string) => {
			setFlash(message);
			await reload(advertiserId);
		},
		[advertiserId, reload],
	);

	return (
		<main className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14">
			<Breadcrumb />
			<Heading />

			<AdvertiserPicker
				draft={draftAdvertiserId}
				setDraft={setDraftAdvertiserId}
				activeAdvertiserId={advertiserId}
				onSubmit={onLoadAdvertiser}
			/>

			{flash && (
				<p className="text-xs text-green-700" role="status">
					{flash}
				</p>
			)}

			{!advertiserId ? (
				<EmptyStatePrompt />
			) : loading ? (
				<LoadingState />
			) : error ? (
				<ErrorState error={error} />
			) : groups.length === 0 ? (
				<NoCapsState
					advertiserId={advertiserId}
					onCreated={() => onCapSaved("New cap created.")}
				/>
			) : (
				<CampaignsList
					advertiserId={advertiserId}
					groups={groups}
					onSaved={onCapSaved}
				/>
			)}

			<DocHint />
		</main>
	);
}

export default AdvertiserCapsPage;

// ---------------------------------------------------------------------------
// Page fragments
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>Advertisers</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Caps</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Advertisers
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Campaign caps
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Throttle conversions, payout spend, and click volume per campaign.
				Caps roll automatically at the end of each window. Paused caps never
				block traffic.
			</p>
		</header>
	);
}

interface AdvertiserPickerProps {
	draft: string;
	setDraft: (value: string) => void;
	activeAdvertiserId: string;
	onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}

function AdvertiserPicker(props: AdvertiserPickerProps) {
	return (
		<section className={`${CARD} flex flex-col gap-4 p-6`}>
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">
					Choose an advertiser
				</h2>
				<p className={HINT}>
					Enter the advertiser id to load their cap configuration. Use
					<span className="font-mono text-zinc-700"> ?advertiser=&lt;id&gt;</span>
					{" "}
					in the URL to deep-link.
				</p>
			</header>
			<form
				onSubmit={props.onSubmit}
				className="flex flex-col gap-3 sm:flex-row sm:items-end"
				aria-label="Advertiser picker"
			>
				<div className="flex flex-1 flex-col gap-1.5">
					<label htmlFor="adv-caps-id" className={LABEL}>
						Advertiser id
					</label>
					<input
						id="adv-caps-id"
						name="advertiserId"
						type="text"
						placeholder="adv_123"
						className={INPUT}
						value={props.draft}
						onChange={(e) => props.setDraft(e.currentTarget.value)}
						aria-describedby="adv-caps-id-hint"
					/>
					<p id="adv-caps-id-hint" className={HINT}>
						{props.activeAdvertiserId
							? `Currently showing caps for ${props.activeAdvertiserId}.`
							: "No advertiser loaded yet."}
					</p>
				</div>
				<button type="submit" className={BTN_PRIMARY}>
					Load caps
				</button>
			</form>
		</section>
	);
}

function EmptyStatePrompt() {
	return (
		<section className={`${CARD} flex flex-col gap-2 p-6`}>
			<p className="text-sm font-medium text-zinc-900">
				Select an advertiser to get started.
			</p>
			<p className={HINT}>
				Caps are scoped to a single advertiser. Enter their id above and press
				{" "}
				<span className="font-mono">Load caps</span>.
			</p>
		</section>
	);
}

function LoadingState() {
	return (
		<section className={`${CARD} p-6 text-sm text-zinc-500`}>
			Loading caps.
		</section>
	);
}

function ErrorState(props: { error: string }) {
	return (
		<section
			className={`${CARD} border-red-200 bg-red-50 p-6 text-sm text-red-700`}
			role="alert"
		>
			{props.error}
		</section>
	);
}

interface NoCapsStateProps {
	advertiserId: string;
	onCreated: () => void;
}

function NoCapsState(props: NoCapsStateProps) {
	return (
		<section className={`${CARD} flex flex-col gap-4 p-6`}>
			<div className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">
					No caps configured
				</h2>
				<p className={HINT}>
					Create a first cap for this advertiser. Leaving campaign id blank
					creates an "All campaigns" cap that applies across the portfolio.
				</p>
			</div>
			<CapEditor
				advertiserId={props.advertiserId}
				campaignId={null}
				existing={null}
				onSaved={props.onCreated}
			/>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Campaign list — one card per group, each with its cap rows.
// ---------------------------------------------------------------------------

interface CampaignsListProps {
	advertiserId: string;
	groups: CampaignGroup[];
	onSaved: (message: string) => Promise<void> | void;
}

function CampaignsList(props: CampaignsListProps) {
	return (
		<section className="flex flex-col gap-6" aria-label="Caps by campaign">
			{props.groups.map((group) => (
				<CampaignCard
					key={group.campaignId ?? "__ALL__"}
					advertiserId={props.advertiserId}
					group={group}
					onSaved={props.onSaved}
				/>
			))}
		</section>
	);
}

interface CampaignCardProps {
	advertiserId: string;
	group: CampaignGroup;
	onSaved: (message: string) => Promise<void> | void;
}

function CampaignCard(props: CampaignCardProps) {
	const title = props.group.campaignId ?? "All campaigns";
	return (
		<section className={`${CARD} flex flex-col gap-4 p-6`}>
			<header className="flex items-center justify-between gap-2">
				<div className="flex flex-col gap-1">
					<h2 className="text-lg font-semibold text-zinc-900">
						{props.group.campaignId
							? `Campaign ${props.group.campaignId}`
							: "All campaigns"}
					</h2>
					<p className={HINT}>
						{props.group.caps.length} cap
						{props.group.caps.length === 1 ? "" : "s"} configured.
					</p>
				</div>
			</header>

			<ul
				aria-label={`Caps for ${title}`}
				className="flex flex-col divide-y divide-zinc-100 rounded-[6px] border border-zinc-200"
			>
				{props.group.caps.map((cap) => (
					<li key={cap.id} className="flex flex-col gap-3 p-4">
						<CapRow cap={cap} />
						<CapEditor
							advertiserId={props.advertiserId}
							campaignId={props.group.campaignId}
							existing={cap}
							onSaved={() => props.onSaved("Cap updated.")}
						/>
					</li>
				))}
			</ul>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Row — consumed/limit progress bar + status badge.
// ---------------------------------------------------------------------------

function CapRow(props: { cap: AdvertiserCapRow }) {
	const percent = progressPercent(props.cap.consumed, props.cap.cap_limit);
	const color = progressColor(percent);
	const display = formatConsumedOverLimit(props.cap);
	return (
		<div className="flex flex-col gap-1.5">
			<div className="flex flex-wrap items-center justify-between gap-2 text-sm">
				<div className="flex flex-wrap items-center gap-2">
					<span className="font-medium text-zinc-800">
						{CAP_TYPE_LABEL[props.cap.cap_type]}
					</span>
					<span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-600">
						{WINDOW_LABEL[props.cap.window_key]}
					</span>
					<StatusBadge status={props.cap.status} />
				</div>
				<span className="font-mono text-xs text-zinc-700">{display}</span>
			</div>
			<div
				className="h-2 w-full overflow-hidden rounded-full bg-zinc-100"
				role="progressbar"
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuenow={Math.round(percent)}
				aria-label={`${CAP_TYPE_LABEL[props.cap.cap_type]} consumption`}
			>
				<div
					className={`h-full ${color}`}
					style={{ width: `${Math.max(2, Math.round(percent))}%` }}
				/>
			</div>
		</div>
	);
}

function StatusBadge(props: { status: CapStatus }) {
	if (props.status === "paused") {
		return (
			<span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
				Paused
			</span>
		);
	}
	return (
		<span className="rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-xs text-green-700">
			Active
		</span>
	);
}

// ---------------------------------------------------------------------------
// Editor — inline form for create + update.
// ---------------------------------------------------------------------------

interface CapEditorProps {
	advertiserId: string;
	campaignId: string | null;
	existing: AdvertiserCapRow | null;
	onSaved: () => Promise<void> | void;
}

interface EditorState {
	values: CapFormValues;
	errors: Record<string, string>;
	submitting: boolean;
	serverError: string | null;
}

function initialEditorState(existing: AdvertiserCapRow | null): EditorState {
	const base: CapFormValues = {
		capType: (existing?.cap_type as CapType) ?? "conversions",
		capLimit: existing ? String(existing.cap_limit) : "100",
		windowKey: (existing?.window_key as WindowKey) ?? "daily",
		status: (existing?.status as CapStatus) ?? "active",
	};
	return {
		values: base,
		errors: {},
		submitting: false,
		serverError: null,
	};
}

function CapEditor(props: CapEditorProps) {
	const [state, setState] = useState<EditorState>(() =>
		initialEditorState(props.existing),
	);
	const [campaignDraft, setCampaignDraft] = useState<string>(
		props.campaignId ?? "",
	);

	useEffect(() => {
		setState(initialEditorState(props.existing));
	}, [props.existing]);

	const disabled = state.submitting;

	const onSubmit = useCallback(
		async (event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const parsed = capFormSchema.safeParse(state.values);
			if (!parsed.success) {
				setState((prev) => ({
					...prev,
					errors: parsed.errors,
					serverError: null,
				}));
				return;
			}
			setState((prev) => ({
				...prev,
				submitting: true,
				errors: {},
				serverError: null,
			}));
			try {
				const campaignId = props.existing
					? props.existing.campaign_id
					: campaignDraft.trim().length > 0
					? campaignDraft.trim()
					: null;
				await upsertAdvertiserCap({
					advertiserId: props.advertiserId,
					campaignId,
					capType: parsed.data.capType,
					capLimit: parsed.data.capLimit,
					windowKey: parsed.data.windowKey,
					status: parsed.data.status,
				});
				setState((prev) => ({ ...prev, submitting: false }));
				await props.onSaved();
			} catch (err) {
				setState((prev) => ({
					...prev,
					submitting: false,
					serverError:
						err instanceof Error ? err.message : "Unable to save cap.",
				}));
			}
		},
		[
			campaignDraft,
			props.advertiserId,
			props.existing,
			props.onSaved,
			state.values,
		],
	);

	return (
		<form
			onSubmit={onSubmit}
			className="flex flex-col gap-3"
			aria-label={props.existing ? "Edit cap" : "Create cap"}
		>
			{props.existing === null && (
				<div className="flex flex-col gap-1.5">
					<label htmlFor="adv-caps-new-campaign" className={LABEL}>
						Campaign id (optional)
					</label>
					<input
						id="adv-caps-new-campaign"
						type="text"
						placeholder="camp_abc — leave blank for all campaigns"
						className={INPUT}
						value={campaignDraft}
						onChange={(e) => setCampaignDraft(e.currentTarget.value)}
						disabled={disabled}
					/>
				</div>
			)}

			<div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
				<div className="flex flex-col gap-1.5">
					<label className={LABEL} htmlFor="adv-caps-type">
						Cap type
					</label>
					<select
						id="adv-caps-type"
						name="capType"
						className={INPUT}
						value={state.values.capType}
						onChange={(e) =>
							setState((prev) => ({
								...prev,
								values: {
									...prev.values,
									capType: e.currentTarget.value as CapType,
								},
								errors: { ...prev.errors, capType: "" },
							}))
						}
						disabled={disabled}
					>
						{CAP_TYPES.map((type) => (
							<option key={type} value={type}>
								{CAP_TYPE_LABEL[type]}
							</option>
						))}
					</select>
					{state.errors.capType && (
						<p className={ERROR_TEXT}>{state.errors.capType}</p>
					)}
				</div>

				<div className="flex flex-col gap-1.5">
					<label className={LABEL} htmlFor="adv-caps-limit">
						Limit
					</label>
					<input
						id="adv-caps-limit"
						name="capLimit"
						type="number"
						min={0}
						step={1}
						className={INPUT}
						value={state.values.capLimit}
						onChange={(e) =>
							setState((prev) => ({
								...prev,
								values: {
									...prev.values,
									capLimit: e.currentTarget.value,
								},
								errors: { ...prev.errors, capLimit: "" },
							}))
						}
						disabled={disabled}
					/>
					{state.errors.capLimit && (
						<p className={ERROR_TEXT}>{state.errors.capLimit}</p>
					)}
				</div>

				<div className="flex flex-col gap-1.5">
					<label className={LABEL} htmlFor="adv-caps-window">
						Window
					</label>
					<select
						id="adv-caps-window"
						name="windowKey"
						className={INPUT}
						value={state.values.windowKey}
						onChange={(e) =>
							setState((prev) => ({
								...prev,
								values: {
									...prev.values,
									windowKey: e.currentTarget.value as WindowKey,
								},
								errors: { ...prev.errors, windowKey: "" },
							}))
						}
						disabled={disabled}
					>
						{WINDOW_KEYS.map((key) => (
							<option key={key} value={key}>
								{WINDOW_LABEL[key]}
							</option>
						))}
					</select>
					{state.errors.windowKey && (
						<p className={ERROR_TEXT}>{state.errors.windowKey}</p>
					)}
				</div>

				<div className="flex flex-col gap-1.5">
					<label className={LABEL} htmlFor="adv-caps-status">
						Status
					</label>
					<select
						id="adv-caps-status"
						name="status"
						className={INPUT}
						value={state.values.status}
						onChange={(e) =>
							setState((prev) => ({
								...prev,
								values: {
									...prev.values,
									status: e.currentTarget.value as CapStatus,
								},
								errors: { ...prev.errors, status: "" },
							}))
						}
						disabled={disabled}
					>
						<option value="active">Active</option>
						<option value="paused">Paused</option>
					</select>
					{state.errors.status && (
						<p className={ERROR_TEXT}>{state.errors.status}</p>
					)}
				</div>
			</div>

			<div className="flex flex-wrap items-center justify-between gap-3">
				<p className={HINT}>
					Submits to
					<span className="font-mono text-zinc-700">
						{" "}
						PUT {advertiserCapsPaths.caps}
					</span>
					.
				</p>
				<div className="flex items-center gap-2">
					<button type="submit" className={BTN_PRIMARY} disabled={disabled}>
						{disabled
							? "Saving."
							: props.existing
							? "Save changes"
							: "Create cap"}
					</button>
					{props.existing && (
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={() => setState(initialEditorState(props.existing))}
							disabled={disabled}
						>
							Reset
						</button>
					)}
				</div>
			</div>
			{state.serverError && (
				<p className={ERROR_TEXT} role="alert">
					{state.serverError}
				</p>
			)}
		</form>
	);
}

// ---------------------------------------------------------------------------
// Footer hint
// ---------------------------------------------------------------------------

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">How caps work</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>
					Each cap is keyed by (advertiser, campaign, cap type, window). Saving
					with the same key updates in place.
				</li>
				<li>
					Pause a cap to disable enforcement temporarily without losing the
					configured limit.
				</li>
				<li>
					The API rolls the consumed counter automatically when the window
					elapses — no cron sweep required.
				</li>
			</ul>
		</aside>
	);
}
